import Box, { BoxProps } from "@mui/material/Box";
import { SxProps } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { FC, ReactEventHandler } from "react";
import LazyLoad from "react-lazyload";
import { HoverVideo } from "./HoverVideo";

export const Image: FC<BoxProps<"img", { component: "img" }>> = (props) => {
  const { sx } = props;

  return (
    <Box
      {...props}
      sx={{
        ...sx,
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain",
        margin: "auto",
      }}
    />
  );
};

// List of errors caused either by malformed video content or browser incompatibility
// We don't want to report them to Sentry since it's not a code problem.
const IGNORED_ERRORS = ["DEMUXER_ERROR_NO_SUPPORTED_STREAMS", "MEDIA_ELEMENT_ERROR"];

export interface IProps {
  url: string;
  /** Thumbnail for videos */
  thumbnail?: string;
  /** Title of deliverable */
  title?: string;
  /** Is the tile currently active */
  isActive?: boolean;
  /** Is the media a video */
  isVideo: boolean;
  /** Is the overlay visible */
  isOverlayVisible?: boolean;
  /** Callback to notify parent that overlay is active */
  onOverlayActive?: (active: boolean) => void;
  /** Blurs the media and holds video playback while edits are being applied */
  isApplyingEdits?: boolean;
  /** Custom styles */
  sx?: SxProps;
}

export const SrMediaThumbnail: FC<IProps> = ({
  url,
  thumbnail,
  title,
  isActive,
  isVideo,
  isOverlayVisible,
  onOverlayActive,
  isApplyingEdits,
  sx,
}) => {
  const { data: hasThumbnail } = useQuery({
    queryKey: [title, thumbnail, "exists"],
    queryFn: async () => {
      if (!thumbnail) {
        return false;
      }
      try {
        const res = await fetch(thumbnail, { method: "HEAD" });
        return res.status === 200;
      } catch (error) {
        console.error("Failed to fetch thumbnail:", error);
        return false;
      }
    },
    enabled: !!thumbnail,
  });

  const handleOnError: ReactEventHandler<HTMLImageElement | HTMLVideoElement> = (e) => {
    const error = (e.target as { error?: { message: string; code: string } })?.error;

    const shouldHandleError =
      error && !IGNORED_ERRORS.some((ignoredError) => error.message?.includes(ignoredError));

    if (shouldHandleError) {
      console.error("Media loading error:", error);
    }
  };

  return (
    <LazyLoad
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        filter: isApplyingEdits ? "blur(4px)" : "none",
      }}
      scrollContainer="#ScrollableContentForLazyLoading"
    >
      {isVideo ? (
        <HoverVideo
          isActive={isActive && !isApplyingEdits}
          isOverlayVisible={isOverlayVisible}
          url={url}
          poster={hasThumbnail ? thumbnail : undefined}
          onOverlayActive={onOverlayActive}
          onError={handleOnError}
          sx={sx}
        />
      ) : (
        <Image component="img" src={url} onError={handleOnError} alt={title || ""} sx={sx} />
      )}
    </LazyLoad>
  );
};
